/**
 * /care · the Care pillar surface.
 *
 * The world's-best-concierge frame for care:
 *   five intent rails — ME · FAMILY · PET · HOME · AUTO — each with
 *   sub-chips. Members say "I need a vet" or "my pipe burst"; we
 *   route them to the right slice of Google's index, then the desk
 *   takes the warm path.
 *
 * Surfaces:
 *   · Two fast-lanes above the rails: 🚨 Emergency (country-aware
 *     local number) and 🩺 House call now (concierge dispatch)
 *   · Rail tabs (Me · My Family · My Pet · My Home · My Car)
 *   · Sub-chip carousel inside each rail
 *   · City search → card grid → drawer with map + Request CTA
 *
 * Embed mode (`?embed=widget`): chrome hidden via embed.css · brief
 * dispatcher routes to Aurelion / MindEscapes where configured.
 *
 * Architecture mirrors /fit (Fit.jsx) — same chrome shape, same
 * drawer pattern, same Request-via-Concierge® primary CTA discipline.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useEmbedMember } from "@/hooks/useEmbedMember";
import { TableFooter } from "@/components/table/TableFooter";
import { PlacesAutocomplete } from "@/components/shared/PlacesAutocomplete";
import ConciergeHero from "@/components/global/ConciergeHero";
import ConciergeOnDutyPill from "@/components/global/ConciergeOnDutyPill";
import ConciergeSurfaceMenu from "@/components/global/ConciergeSurfaceMenu";
import CrossSurfaceNav from "@/components/global/CrossSurfaceNav";
import StaticMapBand from "@/components/shared/StaticMapBand";
import PillarCard from "@/components/shared/PillarCard";

import MemberContextBand from "../components/MemberContextBand";
const API = process.env.REACT_APP_BACKEND_URL || "";

const T = {
  bg:     "#F4EFE6",
  card:   "#FCFAF4",
  ink:    "#211C15",
  ink2:   "#5C5447",
  ink3:   "#8A8071",
  line:   "#E5DCCB",
  line2:  "#D8CDB7",
  brass:  "#8B6914",
  alert:  "#B0212C",
};

function resolveSrc(u) {
  if (!u) return null;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  return `${API}${u.startsWith("/") ? "" : "/"}${u}`;
}

export default function CarePage() {
  const [taxonomy, setTaxonomy]   = useState(null);   // {rails, categories_by_rail}
  const [activeRail, setActiveRail] = useState("me");
  const [category, setCategory]   = useState("doctors");
  const [q, setQ]                 = useState("");
  const [results, setResults]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]         = useState(null);
  const [briefProvider, setBriefProvider] = useState(null);
  const [openDetail, setOpenDetail] = useState(null);
  const [isNarrow, setIsNarrow]   = useState(false);

  // Load taxonomy once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/care/taxonomy`);
        if (!r.ok) throw new Error(`taxonomy_${r.status}`);
        const j = await r.json();
        if (!cancelled) setTaxonomy(j);
      } catch {
        if (!cancelled) setError("Could not load the care directory.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Narrow viewport.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 760px)");
    const u = () => setIsNarrow(mq.matches);
    u();
    mq.addEventListener ? mq.addEventListener("change", u) : mq.addListener(u);
    return () => mq.removeEventListener ? mq.removeEventListener("change", u) : mq.removeListener(u);
  }, []);

  // Drawer Emergency hand-off · listen for the custom event the
  // drawer fires when the member taps "Nearest A&E + dispatch".
  useEffect(() => {
    function onEmergency(e) {
      const d = e.detail || {};
      setBriefProvider({
        _open_emergency: true,
        hint_city: d.city || q.trim() || results?.city_query || "",
        country_code: d.country_code || null,
      });
    }
    window.addEventListener("care:open-emergency", onEmergency);
    return () => window.removeEventListener("care:open-emergency", onEmergency);
  }, [q, results]);

  async function runSearch(query, cat) {
    const term = (query ?? q).trim();
    const useCat = cat ?? category;
    if (!term) return;
    setLoading(true); setError(null); setResults(null);
    try {
      const url = new URL(`${API}/api/care/search`);
      url.searchParams.set("q", term);
      url.searchParams.set("category", useCat);
      const r = await fetch(url.toString());
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `search_failed (${r.status})`);
      setResults(data);
    } catch (e) {
      setError(e.message || "Could not load providers.");
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!results?.next_page_token || loadingMore) return;
    setLoadingMore(true);
    try {
      const url = new URL(`${API}/api/care/search`);
      url.searchParams.set("q", results.city_query);
      url.searchParams.set("category", results.category);
      url.searchParams.set("page_token", results.next_page_token);
      const r = await fetch(url.toString());
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `loadmore_${r.status}`);
      setResults((prev) => ({
        ...prev,
        cards: [...(prev.cards || []), ...(data.cards || [])],
        next_page_token: data.next_page_token || null,
      }));
    } catch (e) {
      setError(e.message || "Could not load more.");
    } finally {
      setLoadingMore(false);
    }
  }

  function pickRail(railCode) {
    setActiveRail(railCode);
    // First chip in that rail becomes active by default
    const cats = taxonomy?.categories_by_rail?.[railCode] || [];
    if (cats.length) {
      setCategory(cats[0].code);
      if (q.trim()) runSearch(q, cats[0].code);
    }
  }

  function pickCategory(code) {
    setCategory(code);
    if (q.trim()) runSearch(q, code);
  }

  const railChips = taxonomy?.categories_by_rail?.[activeRail] || [];

  return (
    <div
      data-testid="care-page-root"
      data-table-surface="care"
      style={{
        background: T.bg, minHeight: "100vh", color: T.ink,
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        "--t-bg": T.bg, "--t-ink": T.ink, "--t-ink2": T.ink2,
        "--t-ink3": T.ink3, "--t-line": T.line, "--t-brass": T.brass,
      }}
    >
      <CareTopBar onHome={() => { setResults(null); setQ(""); }} />
      <MemberContextBand pillar="care" />

      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "12px 28px 80px" }}>
<ConciergeHero
          testid="care-hero"
          eyebrow="Concierge® · Care"
          headline="Who do you need to"
          headlineTail="take care of?"
          subtitle={<>Doctors, vets, plumbers, locksmiths&mdash;every kind of help, anywhere.</>}
          maxWidth={980}
        >
          {/* Two fast-lanes · above the rails */}
          <FastLanes onOpenEmergency={() => setBriefProvider({ _open_emergency: true, hint_city: q.trim() || results?.city_query || "" })}
                     onOpenHouseCall={() => setBriefProvider({ _open_house_call: true, hint_city: q.trim() || results?.city_query || "" })}
                     isNarrow={isNarrow} />

          {/* Rail tabs · ME · FAMILY · PET · HOME · AUTO */}
          {taxonomy && (
            <div
              data-testid="care-rail-row"
              style={{
                display: "flex", justifyContent: "center",
                flexWrap: "wrap", gap: 8, marginBottom: 12,
              }}
            >
              {taxonomy.rails.map((r) => {
                const on = r.code === activeRail;
                return (
                  <button
                    key={r.code}
                    type="button"
                    data-testid={`care-rail-${r.code}`}
                    onClick={() => pickRail(r.code)}
                    style={{
                      background: on ? T.ink : "transparent",
                      color: on ? T.bg : T.ink,
                      border: `1px solid ${on ? T.ink : T.line2}`,
                      borderRadius: 999,
                      padding: isNarrow ? "9px 16px" : "10px 22px",
                      fontSize: 13.5, fontWeight: 600,
                      cursor: "pointer", fontFamily: "inherit",
                      letterSpacing: "0.04em",
                      transition: "all 0.12s",
                    }}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Sub-chip row · inside the active rail */}
          {railChips.length > 0 && (
            <div
              data-testid="care-chip-row"
              style={{
                display: "flex", justifyContent: "center",
                flexWrap: "wrap", gap: 6, marginBottom: 16,
              }}
            >
              {railChips.map((c) => {
                const on = c.code === category;
                return (
                  <button
                    key={c.code}
                    type="button"
                    data-testid={`care-chip-${c.code}`}
                    onClick={() => pickCategory(c.code)}
                    style={{
                      background: on ? T.brass : "transparent",
                      color: on ? T.bg : T.ink2,
                      border: `1px solid ${on ? T.brass : T.line2}`,
                      borderRadius: 999,
                      padding: isNarrow ? "6px 12px" : "7px 14px",
                      fontSize: 12, fontWeight: 500,
                      cursor: "pointer", fontFamily: "inherit",
                      transition: "all 0.12s",
                    }}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* City search */}
          <form
            data-testid="care-search-bar"
            onSubmit={(e) => { e.preventDefault(); runSearch(); }}
            style={{
              background: T.card,
              border: `1px solid ${T.line2}`,
              borderRadius: 18,
              padding: isNarrow ? "12px" : "8px 8px 8px 18px",
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr" : "1fr auto",
              gap: isNarrow ? 10 : 8,
              alignItems: "center",
              maxWidth: 760,
              margin: "0 auto",
              boxShadow: "0 6px 22px rgba(33,28,21,0.04)",
            }}
          >
            <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
              <PlacesAutocomplete
                value={q}
                onChange={setQ}
                onSubmit={(picked) => { setQ(picked); runSearch(picked); }}
                placeholder="City — Lisbon · Paris · Mumbai · New York…"
                types="(cities)"
                testidPrefix="care-search"
                inputStyle={{
                  border: 0, background: "transparent", outline: "none",
                  borderRadius: 0, fontSize: 16, color: T.ink,
                  padding: "10px 12px", fontFamily: "inherit",
                }}
              />
            </div>
            <button
              type="submit"
              data-testid="care-search-submit"
              disabled={loading || !q.trim()}
              style={{
                background: T.ink, color: T.card, border: 0, borderRadius: 14,
                padding: "12px 22px", fontSize: 14, fontWeight: 600,
                cursor: loading ? "wait" : (!q.trim() ? "not-allowed" : "pointer"),
                opacity: (loading || !q.trim()) ? 0.65 : 1,
                fontFamily: "inherit",
                width: isNarrow ? "100%" : "auto",
              }}
            >
              {loading ? "Searching…" : "Find care"}
            </button>
          </form>

          {/* Two CTA row */}
          <div
            data-testid="care-hero-secondary-row"
            style={{
              display: "flex", justifyContent: "center", alignItems: "center",
              gap: 18, marginTop: 18, flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              data-testid="care-hero-ask-concierge"
              onClick={() => setBriefProvider({ _open_ask: true })}
              style={{
                background: "transparent",
                color: T.brass,
                border: `1px solid ${T.brass}`,
                borderRadius: 999,
                padding: "10px 22px",
                fontSize: 13.5, fontWeight: 500,
                cursor: "pointer", fontFamily: "inherit",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.brass; e.currentTarget.style.color = T.bg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.brass; }}
            >
              Ask the Concierge®
            </button>
          </div>
          <p style={{ fontSize: 12.5, color: T.ink3, fontStyle: "italic", marginTop: 14, marginBottom: 0 }}>
            Find a provider directly or ask your Concierge® to arrange the introduction.
          </p>
        </ConciergeHero>

        {/* Results */}
        {error && (
          <p data-testid="care-error" style={{ textAlign: "center", color: T.alert, fontSize: 14 }}>{error}</p>
        )}

        {results && results.cards?.length === 0 && !loading && (
          <div data-testid="care-empty" style={{ textAlign: "center", padding: "48px 20px", color: T.ink2, maxWidth: 620, margin: "0 auto" }}>
            <p style={{ fontSize: 15, marginBottom: 18 }}>
              No live providers found for <strong style={{ color: T.ink }}>{results.city_query}</strong>
              {results.category_label ? <> in <em>{results.category_label}</em></> : null}.
              The Concierge® will find it by hand.
            </p>
            <button
              type="button"
              onClick={() => setBriefProvider({ _open_ask: true, hint_city: results.city_query })}
              style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "12px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            >
              Send a brief
            </button>
          </div>
        )}

        {results && results.cards?.length > 0 && (
          <>
            <div style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 11, letterSpacing: "0.18em",
              color: T.brass, fontWeight: 700, textTransform: "uppercase",
              textAlign: "center", margin: "26px 0 16px",
            }}>
              {results.category_label} · {results.city_query} · {results.cards.length} found
            </div>
            <div
              data-testid="care-results"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 18,
              }}
            >
              {results.cards.map((p, i) => (
                <PillarCard
                  key={p.place_id || i}
                  venue={p}
                  testidPrefix="care"
                  onView={() => setOpenDetail(p)}
                  onBrief={() => setBriefProvider(p)}
                  briefLabel="Request via Concierge®"
                  topLeftPill={p.primary_type_label ? (
                    <span style={{
                      background: "rgba(252,250,244,0.92)", color: T.brass,
                      padding: "3px 9px", borderRadius: 999,
                      fontFamily: "ui-monospace, monospace",
                      fontSize: 10, letterSpacing: "0.14em",
                      fontWeight: 600, textTransform: "uppercase",
                      border: `1px solid ${T.brass}44`,
                    }}>{p.primary_type_label}</span>
                  ) : null}
                  topRightPill={p.open_now != null ? (
                    <span data-testid="care-card-open" style={{
                      background: p.open_now ? "rgba(46,138,82,0.92)" : "rgba(176,33,44,0.92)",
                      color: T.card,
                      padding: "3px 9px", borderRadius: 999,
                      fontFamily: "ui-monospace, monospace",
                      fontSize: 10, letterSpacing: "0.14em", fontWeight: 700,
                    }}>{p.open_now ? "OPEN" : "CLOSED"}</span>
                  ) : null}
                  photoBadge={
                    ((p.category || "").includes("emergency") || (p.category || "") === "vet_emergency")
                      ? <span style={{
                          background: T.alert, color: T.card,
                          padding: "4px 10px", borderRadius: 999,
                          fontFamily: "ui-monospace, monospace",
                          fontSize: 10, letterSpacing: "0.14em", fontWeight: 700,
                        }}>24h · URGENT</span>
                      : null
                  }
                />
              ))}
            </div>
            {results.next_page_token && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 36 }}>
                <button
                  type="button"
                  data-testid="care-load-more"
                  onClick={loadMore}
                  disabled={loadingMore}
                  style={{
                    background: "transparent", color: T.ink,
                    border: `1.5px solid ${T.ink}`, borderRadius: 999,
                    padding: "12px 28px", fontSize: 13.5, fontWeight: 500,
                    cursor: loadingMore ? "wait" : "pointer",
                    fontFamily: "inherit",
                    opacity: loadingMore ? 0.6 : 1,
                  }}
                >
                  {loadingMore ? "Loading…" : `Load more · ${results.cards.length} shown`}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <TableFooter slug="care" />

      {briefProvider && (
        <CareRequestModal
          payload={briefProvider}
          city={results?.city_query || briefProvider.hint_city || ""}
          category={results?.category || category}
          rail={activeRail}
          onClose={() => setBriefProvider(null)}
        />
      )}

      {openDetail && (
        <CareDetailDrawer
          card={openDetail}
          onClose={() => setOpenDetail(null)}
          onRequest={() => { setBriefProvider(openDetail); setOpenDetail(null); }}
        />
      )}
    </div>
  );
}


/* ── Fast-lanes · 🚨 Emergency + 🩺 House call now ───────────────── */
function FastLanes({ onOpenEmergency, onOpenHouseCall, isNarrow }) {
  return (
    <div
      data-testid="care-fast-lanes"
      style={{
        display: "flex", justifyContent: "center",
        gap: 10, marginBottom: 22, flexWrap: "wrap",
      }}
    >
      <button
        type="button"
        data-testid="care-fast-emergency"
        onClick={onOpenEmergency}
        style={{
          background: T.alert, color: T.card,
          border: 0, borderRadius: 14,
          padding: isNarrow ? "10px 16px" : "11px 20px",
          fontSize: 13.5, fontWeight: 700,
          cursor: "pointer", fontFamily: "inherit",
          letterSpacing: "0.06em",
          display: "inline-flex", alignItems: "center", gap: 8,
          boxShadow: "0 4px 14px rgba(176,33,44,0.22)",
        }}
      >
        <span aria-hidden>🚨</span> Emergency · local number
      </button>
      <button
        type="button"
        data-testid="care-fast-housecall"
        onClick={onOpenHouseCall}
        style={{
          background: T.ink, color: T.card,
          border: 0, borderRadius: 14,
          padding: isNarrow ? "10px 16px" : "11px 20px",
          fontSize: 13.5, fontWeight: 700,
          cursor: "pointer", fontFamily: "inherit",
          letterSpacing: "0.06em",
          display: "inline-flex", alignItems: "center", gap: 8,
        }}
      >
        <span aria-hidden>🩺</span> House call now
      </button>
    </div>
  );
}


/* ── Provider card · clickable · parity with /fit VenueCard ────── */
function ProviderCard({ provider, onOpen }) {
  const photoSrc = resolveSrc(provider.photo_url);
  const rating = provider.rating ? Number(provider.rating) : null;
  const ratingCount = provider.user_ratings_total;
  const openNow = provider.open_now;
  const isEmergency = (provider.category || "").includes("emergency") ||
                      (provider.category || "").includes("vet_emergency");
  return (
    <article
      data-testid="care-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); } }}
      style={{
        background: T.card, border: `1px solid ${T.line}`, borderRadius: 16,
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(33,28,21,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
    >
      <div
        data-testid="care-card-photo"
        style={{
          aspectRatio: "4 / 3",
          position: "relative",
          background: photoSrc
            ? `linear-gradient(180deg, rgba(33,28,21,0) 55%, rgba(33,28,21,0.18) 100%), url(${photoSrc}) center/cover no-repeat`
            : `linear-gradient(135deg, ${T.line} 0%, ${T.line2} 60%, ${T.brass}33 100%)`,
        }}
      >
        {!photoSrc && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', 'EB Garamond', serif",
            fontSize: 30, fontWeight: 500, color: T.brass,
          }}>
            {(provider.name || "C").trim().charAt(0).toUpperCase()}
          </div>
        )}
        {provider.primary_type_label && (
          <span
            data-testid="care-card-type-pill"
            style={{
              position: "absolute", top: 12, left: 12,
              background: "rgba(252,250,244,0.92)", color: T.brass,
              padding: "3px 9px", borderRadius: 999,
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.14em",
              fontWeight: 600, textTransform: "uppercase",
              border: `1px solid ${T.brass}44`,
            }}
          >
            {provider.primary_type_label}
          </span>
        )}
        {openNow != null && (
          <span
            data-testid="care-card-open"
            style={{
              position: "absolute", top: 12, right: 12,
              background: openNow ? "rgba(46,138,82,0.92)" : "rgba(176,33,44,0.92)",
              color: T.card,
              padding: "3px 9px", borderRadius: 999,
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.14em",
              fontWeight: 700,
            }}
          >
            {openNow ? "OPEN" : "CLOSED"}
          </span>
        )}
        {isEmergency && (
          <span
            style={{
              position: "absolute", bottom: 12, left: 12,
              background: T.alert, color: T.card,
              padding: "4px 10px", borderRadius: 999,
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.14em",
              fontWeight: 700,
            }}
          >
            24h · URGENT
          </span>
        )}
      </div>
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
        <h3 className="t-serif" style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.22, margin: 0, color: T.ink }}>
          {provider.name || "Untitled provider"}
        </h3>
        <p style={{ fontSize: 11.5, color: T.ink3, margin: 0, lineHeight: 1.4 }}>
          {provider.short_address || provider.address_line || provider.city_name || "—"}
        </p>
        {rating && (
          <p style={{ fontSize: 12, color: T.ink2, margin: 0, display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
            <span style={{ color: T.brass, fontWeight: 700 }}>★</span>
            <span style={{ fontWeight: 600, color: T.ink }}>{rating.toFixed(1)}</span>
            {ratingCount ? <span style={{ color: T.ink3 }}>({ratingCount.toLocaleString()})</span> : null}
          </p>
        )}
      </div>
    </article>
  );
}


/* ── Drawer · provider detail · parity with FitDetailDrawer ─────── */
function CareDetailDrawer({ card, onClose, onRequest }) {
  const [data, setData] = useState(card);
  const [loadingDetail, setLoadingDetail] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!card?.place_id) { setLoadingDetail(false); return; }
      try {
        const r = await fetch(`${API}/api/care/place/${encodeURIComponent(card.place_id)}`);
        if (!r.ok) throw new Error(`detail_${r.status}`);
        const j = await r.json();
        if (cancelled) return;
        setData({ ...card, ...(j.provider || {}) });
      } catch {
        if (!cancelled) setData(card);
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => { cancelled = true; };
  }, [card?.place_id]);

  const photos = (data.photos && data.photos.length ? data.photos : [data.photo_url].filter(Boolean));
  const hero = resolveSrc(photos[0]);
  const rating = data.rating ? Number(data.rating) : null;
  const ratingCount = data.user_ratings_total;
  const openNow = data.open_now;

  return (
    <div
      data-testid="care-drawer-overlay"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(20,16,11,0.55)",
        display: "flex", justifyContent: "flex-end", zIndex: 1000,
      }}
    >
      <aside
        data-testid="care-drawer"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 96vw)",
          background: T.card, color: T.ink,
          height: "100%", overflowY: "auto",
          boxShadow: "-12px 0 36px rgba(0,0,0,0.22)",
          display: "flex", flexDirection: "column",
        }}
      >
        <div
          style={{
            position: "sticky", top: 0, zIndex: 2,
            background: T.card,
            borderBottom: `1px solid ${T.line}`,
            padding: "14px 22px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}
        >
          <span style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: 10, letterSpacing: "0.22em",
            color: T.brass, fontWeight: 600, textTransform: "uppercase",
          }}>
            Care Routing
          </span>
          <button
            type="button"
            data-testid="care-drawer-close"
            onClick={onClose}
            aria-label="Close"
            style={{ background: "transparent", border: 0, fontSize: 20, cursor: "pointer", color: T.ink2, padding: "0 8px" }}
          >×</button>
        </div>

        {hero && (
          <div
            data-testid="care-drawer-hero"
            style={{
              aspectRatio: "16 / 10",
              background: `url(${hero}) center/cover no-repeat`,
            }}
          />
        )}

        <div style={{ padding: "22px 26px 30px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            fontFamily: "ui-monospace, monospace",
            fontSize: 10.5, letterSpacing: "0.20em",
            color: T.brass, fontWeight: 600, textTransform: "uppercase",
            borderBottom: `1px solid ${T.line}`,
            paddingBottom: 12,
          }}>
            <span>✦ Through your Concierge®</span>
            <span>Concierge® · Est 1998</span>
          </div>

          <h2 className="t-serif" data-testid="care-drawer-title"
              style={{ fontSize: 30, lineHeight: 1.1, fontWeight: 500, margin: 0, color: T.ink }}>
            {data.name || "—"}
          </h2>

          <div style={{ fontSize: 13, color: T.ink2, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {data.primary_type_label && (
              <span style={{
                fontFamily: "ui-monospace, monospace",
                fontSize: 10.5, letterSpacing: "0.16em",
                color: T.brass, fontWeight: 600, textTransform: "uppercase",
                border: `1px solid ${T.brass}55`,
                padding: "2px 9px", borderRadius: 999,
              }}>{data.primary_type_label}</span>
            )}
            {data.short_address && <span>{data.short_address}</span>}
            {rating && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: T.brass }}>★</span>
                <strong style={{ color: T.ink }}>{rating.toFixed(1)}</strong>
                {ratingCount ? <span style={{ color: T.ink3 }}>({ratingCount.toLocaleString()})</span> : null}
              </span>
            )}
            {openNow != null && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: openNow ? "#e6f3e9" : "#fbe2e2",
                color: openNow ? "#1e6b32" : "#922222",
                padding: "2px 10px", borderRadius: 999,
                fontSize: 11, fontWeight: 600,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: openNow ? "#2f8a52" : "#c14545" }} />
                {openNow ? "Open now" : "Closed"}
              </span>
            )}
          </div>

          <StaticMapBand lat={data.lat} lng={data.lng} label="On the map" testid="care-drawer-map" />

          {data.amenities && data.amenities.length > 0 && (
            <div data-testid="care-drawer-amenities" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {data.amenities.map((a) => (
                <span
                  key={a.code}
                  data-testid={`care-drawer-amenity-${a.code}`}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    background: `${T.brass}14`, color: T.ink,
                    border: `1px solid ${T.brass}33`,
                    borderRadius: 999,
                    padding: "5px 11px",
                    fontSize: 11.5, fontWeight: 500,
                  }}
                >
                  <span aria-hidden="true">{a.icon}</span>
                  {a.label}
                </span>
              ))}
            </div>
          )}

          {data.editorial_summary && (
            <p data-testid="care-drawer-summary"
               style={{ fontSize: 14.5, lineHeight: 1.55, color: T.ink2, margin: 0 }}>
              {data.editorial_summary}
            </p>
          )}

          {data.nearby_landmarks && data.nearby_landmarks.length > 0 && (
            <div data-testid="care-drawer-landmarks" style={{ fontSize: 12.5, color: T.ink2 }}>
              <span style={{
                fontFamily: "ui-monospace, monospace",
                fontSize: 10, color: T.ink3,
                letterSpacing: "0.16em", textTransform: "uppercase",
                fontWeight: 600, marginRight: 8,
              }}>Nearby</span>
              {data.nearby_landmarks.map((l, i) => (
                <span key={i} style={{ marginRight: 12 }}>
                  <strong style={{ color: T.ink }}>{l.name}</strong>
                  {l.distance_m ? <span style={{ color: T.ink3 }}> · {Math.round(l.distance_m)}m</span> : null}
                </span>
              ))}
            </div>
          )}

          {data.reviews && data.reviews.length > 0 && (
            <div data-testid="care-drawer-reviews" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{
                fontFamily: "ui-monospace, monospace",
                fontSize: 10.5, color: T.brass, fontWeight: 700,
                letterSpacing: "0.18em", textTransform: "uppercase",
                margin: 0,
              }}>Trusted voices</p>
              {data.reviews.slice(0, 3).map((rv, i) => (
                <div
                  key={i}
                  data-testid={`care-drawer-review-${i}`}
                  style={{
                    background: T.bg,
                    border: `1px solid ${T.line}`,
                    borderRadius: 12,
                    padding: "10px 14px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 12 }}>
                    <strong style={{ color: T.ink }}>{rv.author}</strong>
                    {rv.rating ? (
                      <span style={{ color: T.brass, fontWeight: 700 }}>{"★".repeat(Math.round(rv.rating))}</span>
                    ) : null}
                    {rv.time_text ? <span style={{ color: T.ink3 }}>· {rv.time_text}</span> : null}
                  </div>
                  <p style={{ fontSize: 13, color: T.ink2, margin: 0, lineHeight: 1.5 }}>
                    “{rv.text.length > 280 ? rv.text.slice(0, 280).trim() + "…" : rv.text}”
                  </p>
                </div>
              ))}
            </div>
          )}

          <div data-testid="care-drawer-meta" style={{ display: "grid", gap: 6, fontSize: 13 }}>
            {data.address_line && <MetaRow label="Address" value={data.address_line} />}
            {data.phone && (
              <MetaRow label="Phone" value={
                <a href={`tel:${data.phone}`} style={{ color: T.ink, textDecoration: "underline" }}>{data.phone}</a>
              } />
            )}
            {data.website && (
              <MetaRow label="Website" value={
                <a href={data.website} target="_blank" rel="noreferrer noopener" style={{ color: T.brass, textDecoration: "underline" }}>
                  {(data.website || "").replace(/^https?:\/\//, "").replace(/\/.*$/, "")}
                </a>
              } />
            )}
            {data.weekday_hours && data.weekday_hours.length > 0 && (
              <MetaRow label="Hours" value={
                <span style={{ whiteSpace: "pre-line", color: T.ink2 }}>
                  {data.weekday_hours.slice(0, 7).join("\n")}
                </span>
              } />
            )}
          </div>

          {loadingDetail && (
            <p style={{ fontSize: 11.5, color: T.ink3, fontStyle: "italic", margin: 0 }}>
              Loading the full atmosphere…
            </p>
          )}

          <div data-testid="care-drawer-ctas" style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
            <button
              type="button"
              onClick={onRequest}
              data-testid="care-drawer-request"
              style={{
                background: T.ink, color: T.card,
                border: 0, borderRadius: 999,
                padding: "14px 22px",
                fontSize: 14, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
                textAlign: "center",
              }}
            >
              Request via Concierge®
            </button>
            <button
              type="button"
              data-testid="care-drawer-emergency"
              onClick={() => {
                onClose();
                // Hand off to the hero fast-lane modal · we don't
                // have a setBriefProvider here so use a custom event
                // bubble that the parent listens to.
                window.dispatchEvent(new CustomEvent("care:open-emergency", {
                  detail: { city: data.city_name, country_code: data.country_code },
                }));
              }}
              style={{
                background: "transparent", color: T.alert,
                border: `1.5px solid ${T.alert}`, borderRadius: 999,
                padding: "11px 22px",
                fontSize: 13, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
                textAlign: "center",
              }}
            >
              🚨 Nearest A&amp;E + dispatch numbers
            </button>
            {data.google_maps_url && (
              <a
                href={data.google_maps_url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="care-drawer-google"
                style={{
                  background: "transparent", color: T.brass,
                  border: `1.5px solid ${T.brass}`, borderRadius: 999,
                  padding: "12px 22px",
                  fontSize: 13.5, fontWeight: 500,
                  textAlign: "center", textDecoration: "none",
                  fontFamily: "inherit", display: "block",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.brass; e.currentTarget.style.color = T.bg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.brass; }}
              >
                Open in Google Maps ↗
              </a>
            )}
          </div>
          <p style={{ fontSize: 11.5, color: T.ink3, fontStyle: "italic", textAlign: "center", margin: 0, lineHeight: 1.5 }}>
            Ask your Concierge® team to arrange it · we hold the door open.
          </p>
        </div>
      </aside>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 12, alignItems: "start", paddingTop: 4 }}>
      <span style={{
        fontFamily: "ui-monospace, monospace",
        fontSize: 10, color: T.ink3,
        letterSpacing: "0.16em", textTransform: "uppercase",
        fontWeight: 600,
      }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}


/* ── Care request modal · routes through /api/care/request ──────── */
function CareRequestModal({ payload, city, category, rail, onClose }) {
  const isOpenAsk      = !!payload?._open_ask;
  const isEmergency    = !!payload?._open_emergency;
  const isHouseCall    = !!payload?._open_house_call;
  const isProviderBrief = !isOpenAsk && !isEmergency && !isHouseCall;

  const [providerName, setProviderName] = useState(isProviderBrief ? (payload.name || "") : "");
  const [cityVal, setCityVal]           = useState(payload.city_name || payload.hint_city || city || "");
  const embedMember = useEmbedMember();
  const [name, setName]   = useState(embedMember?.name  || "");
  const [email, setEmail] = useState(embedMember?.email || "");
  const [phone, setPhone] = useState(embedMember?.phone || "");
  const [date, setDate]   = useState("");
  const [time, setTime]   = useState("");
  const [urgency, setUrgency] = useState(isEmergency ? "emergency" : (isHouseCall ? "urgent" : "soon"));
  const [persons, setPersons] = useState(1);
  const [petSpecies, setPetSpecies] = useState("");
  const [notes, setNotes] = useState("");
  const [emergencyInfo, setEmergencyInfo] = useState(
    isEmergency
      ? { country: null, numbers: { police: "112", ambulance: "112", fire: "112" },
          note: "International / EU default. Loading local numbers…" }
      : null
  );
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone]   = useState(null);

  // Emergency fast-lane: fetch local numbers immediately
  useEffect(() => {
    if (!isEmergency) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        // Country wins if we have it (from card · cheapest path).
        if (payload.country_code) params.set("country", payload.country_code);
        // Else resolve from the typed/searched city.
        else if (payload.hint_city) params.set("city", payload.hint_city);
        else if (cityVal) params.set("city", cityVal);
        const r = await fetch(`${API}/api/care/emergency?${params.toString()}`);
        const j = await r.json();
        if (!cancelled) setEmergencyInfo(j);
      } catch { /* silent — default 112 stays */ }
    })();
    return () => { cancelled = true; };
  }, [isEmergency, payload?.country_code, payload?.hint_city]);

  const isPetRail = rail === "pet";

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || (!email.trim() && !phone.trim())) {
      setError("Your name plus email or phone — so the Concierge® can reply.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        provider_name: providerName.trim() || (isProviderBrief ? payload.name : null),
        provider_id:   isProviderBrief ? payload.place_id : null,
        city:          cityVal.trim() || null,
        country:       payload.country_code || null,
        category:      category || null,
        rail:          rail || null,
        date:          date || null,
        time:          time.trim() || null,
        urgency:       urgency || null,
        persons:       Number(persons) || 1,
        pet_species:   isPetRail ? petSpecies.trim() || null : null,
        notes:         notes.trim() || null,
        member_name:   name.trim(),
        member_email:  email.trim() || undefined,
        member_phone:  phone.trim() || undefined,
      };
      const r = await fetch(`${API}/api/care/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `submit_failed (${r.status})`);
      setDone(data);
    } catch (e) {
      setError(e.message || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Backdrop onClose={onClose}>
        <div data-testid="care-request-success" style={modalStyle}>
          <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", color: T.brass, fontWeight: 700 }}>BY THE CONCIERGE® · CARE</div>
          <h2 className="t-serif" style={{ fontSize: 26, fontWeight: 500, margin: "10px 0 14px", color: T.ink }}>Your brief is with the Concierge®.</h2>
          <p style={{ fontSize: 14.5, color: T.ink2, lineHeight: 1.55 }}>
            We&apos;ll be in touch on <strong style={{ color: T.ink }}>{email || phone}</strong> very shortly with the next step.
          </p>
          <div data-testid="care-request-ref" style={{ display: "inline-block", padding: "9px 16px", background: T.card, border: `1px solid ${T.brass}`, borderRadius: 999, fontFamily: "ui-monospace, monospace", fontSize: 12, color: T.brass, fontWeight: 600, marginTop: 16 }}>
            {done.display_ref || done.member_reference || "REF-PENDING"}
          </div>
          <div style={{ marginTop: 22 }}>
            <button type="button" onClick={onClose} style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
          </div>
        </div>
      </Backdrop>
    );
  }

  const headline = isEmergency  ? "Emergency · local dispatch"
                 : isHouseCall  ? "House call now"
                 : isOpenAsk    ? "Who do you need?"
                                : "Request via Concierge®";

  return (
    <Backdrop onClose={onClose}>
      <div data-testid="care-request-modal" style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", color: isEmergency ? T.alert : T.brass, fontWeight: 700 }}>BY THE CONCIERGE® · CARE</div>
        <h2 className="t-serif" style={{ fontSize: 24, fontWeight: 500, margin: "10px 0 8px", color: T.ink }}>
          {headline}
        </h2>

        {/* Emergency · show local dispatch numbers up top */}
        {isEmergency && emergencyInfo && (
          <div data-testid="care-emergency-numbers" style={{
            background: "rgba(176,33,44,0.06)",
            border: `1px solid ${T.alert}33`,
            borderRadius: 12, padding: "12px 14px", marginBottom: 16,
          }}>
            <p style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
              color: T.alert, fontWeight: 700, margin: "0 0 8px",
            }}>
              Dial now · {emergencyInfo.country
                          ? `${emergencyInfo.country}${emergencyInfo.city ? ` · ${emergencyInfo.city}` : ""}`
                          : "international"}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 13.5 }}>
              {Object.entries(emergencyInfo.numbers || {}).map(([k, v]) => (
                <a key={k} href={`tel:${v}`} style={{ color: T.alert, fontWeight: 700, textDecoration: "none" }}>
                  {k}: <strong>{v}</strong>
                </a>
              ))}
            </div>
            {emergencyInfo.nearest_er && (
              <div
                data-testid="care-emergency-nearest-er"
                style={{
                  marginTop: 12, padding: "10px 12px",
                  background: T.card,
                  border: `1px solid ${T.alert}33`,
                  borderRadius: 10,
                  fontSize: 13,
                }}
              >
                <p style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 10, color: T.alert,
                  letterSpacing: "0.16em", textTransform: "uppercase",
                  fontWeight: 700, margin: "0 0 4px",
                }}>
                  Nearest A&amp;E{emergencyInfo.nearest_er.eta_drive_min ? ` · ${emergencyInfo.nearest_er.eta_drive_min} min by car` : ""}
                </p>
                <p style={{ margin: "0 0 2px", color: T.ink, fontWeight: 600 }}>
                  {emergencyInfo.nearest_er.name}
                </p>
                <p style={{ margin: 0, color: T.ink3, fontSize: 12 }}>
                  {emergencyInfo.nearest_er.short_address || emergencyInfo.nearest_er.address_line}
                </p>
                <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
                  {emergencyInfo.nearest_er.phone && (
                    <a
                      href={`tel:${emergencyInfo.nearest_er.phone}`}
                      data-testid="care-emergency-nearest-er-phone"
                      style={{ color: T.alert, fontWeight: 600, fontSize: 12, textDecoration: "none" }}
                    >
                      ☎ {emergencyInfo.nearest_er.phone}
                    </a>
                  )}
                  {emergencyInfo.nearest_er.google_maps_url && (
                    <a
                      href={emergencyInfo.nearest_er.google_maps_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      data-testid="care-emergency-nearest-er-directions"
                      style={{ color: T.brass, fontWeight: 600, fontSize: 12, textDecoration: "none" }}
                    >
                      Directions ↗
                    </a>
                  )}
                </div>
              </div>
            )}
            <p style={{ fontSize: 12, color: T.ink2, margin: "10px 0 0", fontStyle: "italic" }}>
              {emergencyInfo.note}
            </p>
          </div>
        )}

        <p style={{ fontSize: 13.5, color: T.ink2, margin: "0 0 18px" }}>
          {isProviderBrief ? (payload.name || "")
            : isHouseCall  ? "Tell us where you are and what's needed — we'll dispatch a vetted MD."
            : isEmergency  ? "Numbers above are immediate. Below — log the brief with us so we follow up."
                           : "Tell us what you need — we'll match the right provider."}
        </p>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {(isOpenAsk || isHouseCall || isEmergency) && (
            <Input value={providerName} onChange={setProviderName} placeholder="Provider / what you need (optional)" testid="care-modal-provider" />
          )}
          <Input value={cityVal} onChange={setCityVal} placeholder="City / where you are" testid="care-modal-city" />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={date} onChange={setDate} placeholder="Date" testid="care-modal-date" type="date" />
            <Input value={time} onChange={setTime} placeholder="Time" testid="care-modal-time" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <select
              data-testid="care-modal-urgency"
              value={urgency}
              onChange={(e) => setUrgency(e.target.value)}
              style={inputStyle}
            >
              <option value="routine">Routine</option>
              <option value="soon">Soon</option>
              <option value="urgent">Urgent</option>
              <option value="emergency">Emergency</option>
            </select>
            <Input value={persons} onChange={setPersons} placeholder="People" testid="care-modal-persons" type="number" />
          </div>

          {isPetRail && (
            <Input value={petSpecies} onChange={setPetSpecies} placeholder="Pet (e.g. dog · cat · rabbit)" testid="care-modal-pet" />
          )}

          <Input value={name} onChange={setName} placeholder="Your name" testid="care-modal-name" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={email} onChange={setEmail} placeholder="Email" testid="care-modal-email" type="email" />
            <Input value={phone} onChange={setPhone} placeholder="Phone / WhatsApp" testid="care-modal-phone" />
          </div>

          <textarea
            data-testid="care-modal-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything else? Symptoms, language preference, accessibility, prescription needs…"
            rows={3}
            style={{ ...inputStyle, resize: "vertical", minHeight: 80 }}
          />

          {error && <p data-testid="care-modal-error" style={{ color: "#9a3535", fontSize: 13, margin: 0 }}>{error}</p>}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
            <button type="button" onClick={onClose} style={{ background: "transparent", color: T.ink2, border: `1px solid ${T.line2}`, borderRadius: 999, padding: "10px 18px", fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button type="submit" data-testid="care-modal-submit" disabled={busy} style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.65 : 1, fontFamily: "inherit" }}>
              {busy ? "Sending…" : "Send to Concierge® →"}
            </button>
          </div>
        </form>
      </div>
    </Backdrop>
  );
}

function Backdrop({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(33,28,21,0.55)", zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      {children}
    </div>
  );
}

const modalStyle = {
  background: T.bg, color: T.ink, borderRadius: 18, padding: "28px 28px 22px",
  width: "min(560px, 100%)", maxHeight: "92vh", overflowY: "auto",
  boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
};

const inputStyle = {
  width: "100%", padding: "11px 14px", border: `1px solid ${T.line2}`,
  borderRadius: 12, background: T.card, color: T.ink, fontSize: 14.5,
  fontFamily: "inherit", outline: "none", boxSizing: "border-box",
};

function Input({ value, onChange, placeholder, testid, type = "text" }) {
  return (
    <input
      type={type}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={inputStyle}
    />
  );
}


/* ── Care top bar · sticky · brass active state on /care link ──── */
function CareTopBar({ onHome }) {
  return (
    <header
      data-testid="care-header"
      style={{
        borderBottom: `1px solid ${T.line}`,
        background: T.bg,
        position: "sticky", top: 0, zIndex: 20,
      }}
    >
      <div
        style={{
          maxWidth: 1180, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 22px", gap: 10, flexWrap: "nowrap", minWidth: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
          <Link
            to="/"
            onClick={(e) => { if (onHome) { e.preventDefault(); onHome(); } }}
            data-testid="care-home-button"
            style={{ textDecoration: "none", color: T.ink, display: "inline-flex", alignItems: "baseline", gap: 10, minWidth: 0 }}
          >
            <span className="t-serif" style={{ fontSize: 22, fontWeight: 500, letterSpacing: "0.02em" }}>/care</span>
            <span
              data-testid="care-header-by-concierge"
              className="lc-mobile-hide-on-narrow"
              style={{
                fontFamily: "ui-monospace, monospace",
                fontSize: 11, letterSpacing: "0.22em",
                textTransform: "uppercase", color: T.ink3,
                whiteSpace: "nowrap",
              }}
            >
              by Concierge®
            </span>
          </Link>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <ConciergeOnDutyPill context="care" />
          <CrossSurfaceNav testidPrefix="care-header-nav" size="compact" />
          <ConciergeSurfaceMenu testid="care-surface-menu" />
        </div>
      </div>
    </header>
  );
}
